import React, { useMemo, useState } from 'react';
import DrawingGalleryCard from './DrawingGalleryCard';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PAGE_SIZE = 48;

const GalleryGrid = ({ drawings, loading, error }) => {
  const [page, setPage] = useState(1);

  // Reset page when drawings change (filter/search)
  const totalPages = Math.ceil((drawings?.length || 0) / PAGE_SIZE);

  const visibleDrawings = useMemo(() => {
    const start = 0;
    const end = page * PAGE_SIZE;
    return drawings?.slice(start, end) || [];
  }, [drawings, page]);

  const hasMore = page < totalPages;

  if (loading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
        {Array.from({ length: 12 })?.map((_, i) => (
          <div key={i} className="bg-card border border-border rounded-xl overflow-hidden animate-pulse">
            <div className="h-40 bg-muted" />
            <div className="p-3 space-y-2">
              <div className="h-3 bg-muted rounded w-3/4" />
              <div className="h-3 bg-muted rounded w-1/2" />
              <div className="h-3 bg-muted rounded w-2/3" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-14 h-14 rounded-full bg-error/10 flex items-center justify-center mb-4">
          <Icon name="AlertCircle" size={28} color="var(--color-error)" />
        </div>
        <h3 className="text-base font-semibold text-foreground mb-1">Failed to load drawings</h3>
        <p className="text-sm text-muted-foreground max-w-sm">{error}</p>
      </div>
    );
  }

  if (!drawings?.length) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
          <Icon name="LayoutGrid" size={32} color="var(--color-muted-foreground)" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">No drawings found</h3>
        <p className="text-sm text-muted-foreground max-w-xs">
          Try adjusting your search or filter criteria, or upload a drawing to get started.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
        {visibleDrawings?.map(drawing => (
          <DrawingGalleryCard key={drawing?.id} drawing={drawing} />
        ))}
      </div>

      {/* Load more */}
      {hasMore && (
        <div className="flex flex-col items-center mt-8 gap-2">
          <p className="text-sm text-muted-foreground">
            Showing {visibleDrawings?.length} of {drawings?.length} drawings
          </p>
          <Button
            variant="outline"
            iconName="ChevronDown"
            onClick={() => setPage(p => p + 1)}
          >
            Load more
          </Button>
        </div>
      )}

      {!hasMore && drawings?.length > PAGE_SIZE && (
        <p className="text-center text-xs text-muted-foreground mt-6">
          All {drawings?.length} drawings loaded
        </p>
      )}
    </div>
  );
};

export default GalleryGrid;
